<script setup>
// Екран замість плеєра, коли ефір вимкнено тумблером в адмінці.
//
// Показує не тільки «стріму немає», а й вердикт доступу наперед: бекенд
// рахує його однаково незалежно від того, йде ефір чи ні, тож глядач може
// увійти й дізнатися, чи пустить його гейт, ще до початку трансляції —
// замість того, щоб з'ясовувати це вже на старті.
const props = defineProps({
  access: { type: Object, required: true }
})

const emit = defineEmits(['replay'])

// Скільки ще можна дивитись запис. Лічильник тікає раз на пів хвилини: точніше
// не потрібно, а бекенд сам перестане віддавати запис, коли час мине
const now = ref(Date.now())
let timer = null

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now()
  }, 30000)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

const replayLeft = computed(() => {
  const until = props.access.replay?.until
  if (!until) return ''

  const minutes = Math.ceil((new Date(until).getTime() - now.value) / 60000)
  if (minutes <= 0) return ''
  if (minutes < 60) return `${minutes} хв`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} год ${rest} хв` : `${hours} год`
})

// Ефір без обмежень — перевіряти нічого, входити теж нема навіщо
const restricted = computed(() => !!props.access.restricted)

const allowed = computed(() => restricted.value && props.access.allowed)

// Увійшов, але жодної з потрібних підписок не знайшлось
const rejected = computed(
  () => restricted.value && props.access.authenticated && !props.access.allowed
)
</script>

<template>
  <!--
    Скрол — на зовнішньому блоці, центрування — на внутрішньому (min-h-full):
    на телефоні з відкритим чатом плеєру лишається 45% висоти, і при
    justify-center на самому скролері верх екрана виявився б недосяжним.
    Верхній відступ більший за нижній — щоб заголовок не ліз під плашку назви
    й кнопку «На сайт», які висять над відео
  -->
  <div class="h-full overflow-y-auto">
    <div
      class="flex flex-col justify-center items-center gap-[20px] px-[20px] pt-[64px] pb-[40px] min-h-full text-center"
    >
      <div class="flex flex-col items-center gap-3">
        <span class="flex bg-base-content/30 rounded-full w-3 h-3"></span>
        <p class="font-semibold text-lg">Ефір офлайн</p>
        <p class="opacity-60 text-sm">
          Зараз трансляції немає. Заходьте пізніше.
        </p>
      </div>

      <!-- Запис щойно закінченого ефіру: ще дві години після вимкнення -->
      <div
        v-if="replayLeft"
        class="flex flex-col items-center gap-2"
      >
        <button
          class="gap-2 btn btn-primary"
          @click="emit('replay')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
          Дивитись запис ефіру
        </button>
        <p class="opacity-60 text-xs">Запис буде доступний ще {{ replayLeft }}</p>
      </div>

      <!-- Вердикт доступу: чи пустить гейт, коли ефір увімкнуть -->
      <div
        class="bg-base-200 px-[16px] py-[12px] rounded-lg max-w-[520px] text-sm"
      >
        <template v-if="!restricted">
          <p class="font-medium text-success">Ефір відкритий для всіх</p>
          <p class="opacity-70 mt-1">
            Підписка не потрібна — заходьте, коли трансляція почнеться.
          </p>
        </template>

        <template v-else-if="allowed">
          <p class="font-medium text-success">У вас є доступ до ефіру</p>
          <p class="opacity-70 mt-1">
            <template v-if="access.hasTwitchSub && access.hasMono">
              Знайдено підписку Twitch і Монобазу.
            </template>
            <template v-else-if="access.hasTwitchSub">
              Знайдено підписку Twitch<template v-if="access.twitchLogin">
                — <b>{{ access.twitchLogin }}</b></template
              >.
            </template>
            <template v-else> Знайдено Монобазу. </template>
          </p>
        </template>

        <template v-else-if="rejected">
          <p class="font-medium text-error">Доступу до ефіру немає</p>
          <p class="opacity-70 mt-1">
            <template v-if="access.twitchLogin">
              Ви увійшли як <b>{{ access.twitchLogin }}</b
              >, але
            </template>
            <template v-else> Ви увійшли, але </template>
            <template v-if="access.needsTwitch && access.needsMono">
              ні активної підписки Twitch, ні Монобази не знайдено.
            </template>
            <template v-else-if="access.needsTwitch">
              активної підписки Twitch не знайдено.
            </template>
            <template v-else> Монобази не знайдено. </template>
            Статус оновлюється при вході — оформіть підписку й увійдіть ще раз.
          </p>
        </template>

        <template v-else>
          <p class="font-medium text-warning">Доступ не перевірено</p>
          <p class="opacity-70 mt-1">
            <template v-if="access.needsTwitch && access.needsMono">
              Дивитися зможуть підписники Twitch-каналу
              <a
                href="https://www.twitch.tv/olsior"
                target="_blank"
                class="link"
                >Olsior</a
              >
              або учасники
              <a
                href="https://base.monobank.ua/olsior#subscriptions"
                target="_blank"
                class="link"
                >Монобази</a
              >.
            </template>
            <template v-else-if="access.needsTwitch">
              Дивитися зможуть підписники Twitch-каналу
              <a
                href="https://www.twitch.tv/olsior"
                target="_blank"
                class="link"
                >Olsior</a
              >.
            </template>
            <template v-else>
              Дивитися зможуть учасники
              <a
                href="https://base.monobank.ua/olsior#subscriptions"
                target="_blank"
                class="link"
                >Монобази</a
              >.
            </template>
            Увійдіть, щоб перевірити доступ заздалегідь.
          </p>
        </template>
      </div>

      <!--
        Кнопки входу лишаємо й тим, кого вже пускає: перевірити ще раз зайвим
        не буде, а от коли ефір відкритий для всіх — входити нема навіщо
      -->
      <LiveLoginButtons v-if="restricted" :access="access" />
    </div>
  </div>
</template>
